/**
 * Firebase TOTP (Time-based One-Time Password) multi-factor enrollment
 * + verification helpers.
 *
 * R-142-AUTH-UX Phase B.
 *
 * The Firebase Auth `multiFactor` SDK exposes:
 *   - `multiFactor(user).getSession()` — needed to bootstrap a new
 *     enrollment
 *   - `TotpMultiFactorGenerator.generateSecret(session)` — returns a
 *     `TotpSecret` carrying the shared key, period, code length, and
 *     OTP auth URL for the QR code
 *   - `TotpMultiFactorGenerator.assertionForEnrollment(secret, code)`
 *     — produces an assertion the user must present (with their freshly
 *     entered 6-digit code) to finalize enrollment
 *   - `multiFactor(user).enroll(assertion, displayName)` — completes
 *     enrollment and persists the second factor server-side
 *
 * Sign-in challenge path (used by TwoFactorChallenge component):
 *   - Catch `auth/multi-factor-auth-required` from signInWithEmailAndPassword
 *   - Extract `MultiFactorResolver` via `getMultiFactorResolver(auth, error)`
 *   - Render the TOTP hint, get user's 6-digit code
 *   - `TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code)`
 *   - `resolver.resolveSignIn(assertion)` finishes the sign-in
 *
 * All helpers re-throw underlying Firebase errors unchanged so the
 * caller can map them to user-facing toasts.
 */
import type {
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  MultiFactorSession,
  TotpSecret,
  User,
} from 'firebase/auth';
import {
  TotpMultiFactorGenerator,
  getMultiFactorResolver,
  multiFactor,
} from 'firebase/auth';

import { auth } from '../../firebase';

export interface TotpEnrollmentStart {
  secret: TotpSecret;
  qrCodeUrl: string;
  manualEntryKey: string;
  session: MultiFactorSession;
}

const ISSUER = 'Datacrate';

/**
 * Begin a TOTP enrollment. Returns the QR-code URL that the front-end
 * renders, the human-readable manual entry key (for typing into an
 * authenticator app that can't scan QR codes), and the underlying
 * Firebase session token that `completeTotpEnrollment` consumes.
 */
export async function startTotpEnrollment(user: User = requireUser()): Promise<TotpEnrollmentStart> {
  const mfa = multiFactor(user);
  const session = await mfa.getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  const accountName = user.email ?? user.uid;
  const qrCodeUrl = secret.generateQrCodeUrl(accountName, ISSUER);
  return {
    secret,
    qrCodeUrl,
    manualEntryKey: secret.secretKey,
    session,
  };
}

/**
 * Complete TOTP enrollment. The user enters the 6-digit code displayed
 * by their authenticator app; on success the factor is persisted and
 * the user is prompted on every subsequent sign-in.
 *
 * `displayName` is the user-facing label for this factor — Firebase
 * shows it in the MFA challenge UI; we recommend the device name
 * (e.g. "iPhone 15 — 1Password").
 */
export async function completeTotpEnrollment(
  secret: TotpSecret,
  oneTimeCode: string,
  displayName: string,
  user: User = requireUser(),
): Promise<void> {
  const code = oneTimeCode.replace(/\D/g, '').trim();
  if (code.length !== 6) {
    throw new Error('Enter the 6-digit code from your authenticator app');
  }
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
  await multiFactor(user).enroll(assertion, displayName.trim() || 'Authenticator');
}

/**
 * List the enrolled factors for the current user. Used by
 * `SecuritySettingsPanel` to show enrollment status + each factor's
 * display name + enrollment time.
 */
export function listEnrolledFactors(user: User = requireUser()): MultiFactorInfo[] {
  return multiFactor(user).enrolledFactors;
}

/**
 * Disable a previously enrolled TOTP factor. Removes the factor from
 * the user's profile and downgrades future sign-ins to single-factor.
 */
export async function disableTotpEnrollment(
  factorUid: string,
  user: User = requireUser(),
): Promise<void> {
  await multiFactor(user).unenroll(factorUid);
}

/**
 * Detect whether a thrown error from `signInWithEmailAndPassword`
 * (or any other signIn variant) indicates that the account is gated
 * behind MFA. Returns `null` for any other error.
 */
export function isMfaRequiredError(error: unknown): error is MultiFactorError {
  return (
    typeof error === 'object'
    && error !== null
    && 'code' in error
    && (error as { code?: string }).code === 'auth/multi-factor-auth-required'
  );
}

/**
 * Pull a `MultiFactorResolver` from an MFA-required sign-in error.
 * The resolver carries the hints (one per enrolled factor) and
 * exposes `resolveSignIn()` to finalize the sign-in after a successful
 * TOTP challenge.
 */
export function resolverFromError(error: MultiFactorError): MultiFactorResolver {
  return getMultiFactorResolver(auth, error);
}

/**
 * Complete an MFA challenge during sign-in. Pulls the first TOTP hint
 * from the resolver, generates the assertion with the user-entered
 * code, and resolves the sign-in.
 */
export async function completeMfaChallenge(
  resolver: MultiFactorResolver,
  oneTimeCode: string,
): Promise<void> {
  const code = oneTimeCode.replace(/\D/g, '').trim();
  if (code.length !== 6) {
    throw new Error('Enter the 6-digit code from your authenticator app');
  }
  const totpHint = resolver.hints.find(
    (h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID,
  );
  if (!totpHint) {
    throw new Error('No TOTP factor enrolled on this account');
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code);
  await resolver.resolveSignIn(assertion);
}

function requireUser(): User {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No signed-in user — call multiFactor helpers after sign-in');
  }
  return user;
}
